import { useMemo } from "react";
import { mathPretty, previewTokens, type TableData } from "@/components/editor/table-grid/tableModel";
import { cellColspan, cellRowspan, cellText, tableWidth } from "@/components/editor/table-grid/tableCells";
import { headerWithUnit, planColumns } from "@/components/editor/table-grid/tableFormat";
import { DecimalNumber, planCell, plannedAlign } from "@/components/editor/table-grid/DecimalNumber";
import { bandedRow, densityScale, type PaperTableStyle } from "@/lib/themes/tables";
import { groupStarts, indexInGroup, noteMarks, validGroups } from "@/components/editor/table-grid/tableLayout";
import { GroupHeaderRow, NoteMark, TableNotes } from "@/components/editor/table-grid/TableLayoutParts";
import { testIds } from "@/testIds";

/**
 * The Preview surface — the table as it will read, not as it is typed.
 *
 * The rules follow the borders preset rather than the browser default, because
 * booktabs' whole point is that it does NOT draw a box: a top rule, a rule
 * under the header, a bottom rule, and no vertical lines at all. A preview
 * showing gridlines for a booktabs table would be lying about the output.
 *
 * Cells render through previewTokens, so `\textbf{…}` is bold and `$…$` is
 * maths-ish rather than literal. This is not a TeX renderer and does not
 * pretend to be one — it is the shape of the thing, one step closer than the
 * raw source.
 */

/** A cell's text as it reads: `\textbf{…}` bold, `$…$` as maths. */
export function PreviewCellText({ value }: { value: string }) {
  return (
    <>
      {previewTokens(value).map((t, i) =>
        t.kind === "bold" ? (
          <strong key={i}>{t.value}</strong>
        ) : t.kind === "math" ? (
          <em key={i} style={{ fontFamily: "var(--lilia-font-serif)" }}>
            {mathPretty(t.value)}
          </em>
        ) : (
          <span key={i}>{t.value}</span>
        ),
      )}
    </>
  );
}

const ALIGN = { l: "left", c: "center", r: "right", decimal: "center" } as const;

/**
 * With `paper` (Preview as, document themes 2a) the table is drawn on that
 * paper as it prints there: header face and weight, top rule colour, caption
 * label colour and face, body face, and the paper's table style, density and
 * caption position. Content, widths and alignment never change.
 */
export function TablePreviewPane({
  data,
  paper = null,
  number = null,
}: {
  data: TableData;
  paper?: PaperTableStyle | null;
  /** The number this table prints with in that paper, once compiled. */
  number?: string | null;
}) {
  const { borders } = data;
  const ink = paper ? paper.ink : "var(--lilia-text)";
  const rule = `1px solid ${ink}`;
  const hair = "1px solid var(--lilia-border-strong)";
  const grid = borders === "grid";
  const scale = densityScale(paper?.density ?? "normal");
  const headFill = paper?.headFill ?? null;
  const bodyRows = data.rows.length;
  // The columns' meaning (2g): decimal alignment, units in the header, bold best.
  const width = tableWidth(data.header, data.rows, data.hasHeader);
  const plan = useMemo(() => planColumns(data, width), [data, width]);
  // …and the rest of it: group headers, notes, row groups (tableLayout.ts).
  const groups = useMemo(() => validGroups(data, width), [data, width]);
  const { marks, notes } = useMemo(() => noteMarks(data), [data]);
  const starts = useMemo(() => groupStarts(data), [data]);
  const topRule = borders === "booktabs" && !headFill ? (paper?.rule ? `1.5px solid ${paper.rule}` : rule) : undefined;

  const cellBase = (c: number): React.CSSProperties => ({
    padding: `${Math.round(6 * scale.row)}px ${Math.round(12 * scale.padX)}px`,
    textAlign: plannedAlign(plan[c], ALIGN[data.align[c] ?? "l"]),
    borderLeft: grid && c > 0 ? hair : undefined,
    whiteSpace: "nowrap",
  });

  const caption = data.caption.trim() ? (
    <div
      className="max-w-[42ch] text-center text-[12.5px]"
      style={{ color: paper ? paper.ink : "var(--lilia-text-secondary)", fontFamily: paper ? paper.bodyFont : "var(--lilia-font-serif)" }}
      data-testid={testIds.tableTool.previewCaption}
      data-position={paper?.caption ?? "below"}
    >
      <strong style={{ fontWeight: 700, color: paper?.label, fontFamily: paper?.labelFont }}>
        {paper ? `Table${number ? ` ${number}` : ""}.` : "Table 1:"}{" "}
      </strong>
      <PreviewCellText value={data.caption} />
    </div>
  ) : null;

  return (
    <div className="flex flex-col items-center gap-3 p-6" data-testid={testIds.tableTool.preview} data-paper-style={paper?.style} data-theme={paper?.themeId}>
      <div
        className={paper ? "flex flex-col items-center gap-3 rounded-[3px] px-6 py-5" : "contents"}
        style={paper ? { background: paper.paper, boxShadow: "0 1px 2px rgba(16,24,40,.08), 0 6px 18px rgba(16,24,40,.08)" } : undefined}
      >
        {paper?.caption === "above" && caption}
        <table
          style={{
            borderCollapse: "collapse",
            fontFamily: paper ? paper.bodyFont : "var(--lilia-font-serif)",
            fontSize: 13.5 * scale.font,
            color: ink,
            border: grid ? hair : undefined,
          }}
        >
          {data.hasHeader && (
            <thead>
              {groups.length > 0 && (
                <GroupHeaderRow
                  groups={groups}
                  width={width}
                  ruleColor={headFill ? paper!.headInk : ink}
                  cellStyle={{ ...cellBase(0), borderLeft: undefined, fontWeight: paper?.headWeight ?? 700, borderTop: topRule, background: headFill ?? undefined, color: headFill ? paper!.headInk : undefined, fontFamily: paper?.headFont }}
                />
              )}
              <tr data-part="head" style={{ background: headFill ?? undefined, color: headFill ? paper!.headInk : undefined, fontFamily: paper?.headFont }}>
                {data.header.map((h, c) => (
                  <th
                    key={c}
                    scope="col"
                    colSpan={cellColspan(h) > 1 ? cellColspan(h) : undefined}
                    rowSpan={cellRowspan(h) > 1 ? cellRowspan(h) : undefined}
                    style={{
                      ...cellBase(c),
                      fontWeight: paper?.headWeight ?? 700,
                      // The top rule sits above the group header row when there is one.
                      borderTop: groups.length ? undefined : topRule,
                      borderBottom: borders === "none" || headFill ? undefined : borders === "grid" ? hair : rule,
                    }}
                  >
                    <PreviewCellText value={headerWithUnit(cellText(h), plan[c]?.unit)} />
                    <NoteMark mark={marks.get(`-1:${c}`)} />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {data.rows.map((row, r) => {
              // Banding restarts after each row group's rule, as lilia-theme.sty does.
              const band = paper?.band && bandedRow(indexInGroup(r, starts), bodyRows) ? paper.band : undefined;
              const groupStart = starts.has(r);
              return (
                <tr key={r} data-band={band ? "true" : undefined} data-group-start={groupStart || undefined} style={{ background: band }}>
                  {row.map((v, c) => {
                    const cell = planCell(plan[c], cellText(v));
                    return (
                    <td
                      key={c}
                      data-best={cell.best || undefined}
                      colSpan={cellColspan(v) > 1 ? cellColspan(v) : undefined}
                      rowSpan={cellRowspan(v) > 1 ? cellRowspan(v) : undefined}
                      style={{
                        ...cellBase(c),
                        // A row group's rule: booktabs' \midrule, a second line in a
                        // full grid, a little space with no rules.
                        borderTop: groupStart ? (grid ? `3px double ${ink}` : borders === "booktabs" ? `0.75px solid ${ink}` : undefined) : grid && r > 0 ? hair : undefined,
                        paddingTop: groupStart && borders === "none" ? Math.round(6 * scale.row) + 6 : undefined,
                        borderBottom:
                          borders === "booktabs" && r === data.rows.length - 1 ? rule : undefined,
                      }}
                    >
                      {cell.number ? (
                        <DecimalNumber n={cell.number} plan={plan[c]} bold={cell.best} />
                      ) : cell.best ? (
                        <strong><PreviewCellText value={cell.text} /></strong>
                      ) : (
                        <PreviewCellText value={cell.text} />
                      )}
                      <NoteMark mark={marks.get(`${r}:${c}`)} />
                    </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Notes print under the table (threeparttable), before a caption set below. */}
        <TableNotes notes={notes} style={{ alignSelf: "stretch", color: ink, fontFamily: paper ? paper.bodyFont : "var(--lilia-font-serif)" }} />
        {paper?.caption !== "above" && caption}
      </div>
    </div>
  );
}
